import {
    ChatbotElementInterface,
    ChatbotElementPartInterface,
    ChatbotLinkInterface,
    ChatbotVideoInterface
} from './interfaces';

export class ChatbotAnswerParseService {
    protected static lineSplitPattern = /\r?\n/;
    protected static unorderedListPattern = /^\s?[*](?=[^*])\s?(.*)/g; // starts with * but not **
    protected static orderedListPattern = /^\s?\d+\.\s?(.*)/g; // starts with 1.

    static textDecorationReplacements = [
        {
            regExp: /\*\*([^*]+)\*\*/g,
            replace: '<b>$1</b>'
        },
        {
            regExp: /_([^_]+)_/g,
            replace: '<i>$1</i>'
        }
    ];

    private dialogPath = '';

    constructor(
        private links: ChatbotLinkInterface[],
        private videos: ChatbotVideoInterface[]
    ) {}

    public parseAnswer = (text: string, dialogPath: string): ChatbotElementInterface[] => {
        this.dialogPath = dialogPath;
        const lines = ChatbotAnswerParseService.splitIntoLines(text);
        const elements = this.createElements(lines);
        return ChatbotAnswerParseService.groupElements(elements);
    };

    private static splitIntoLines = (answerText: string): string[] =>
        answerText
            .split(ChatbotAnswerParseService.lineSplitPattern)
            .map((value: string) => value.trim())
            .filter((part: string) => part.length);


    private createElements = (lines: string[]): ChatbotElementInterface[] =>
        lines.map((line: string) => {
            const unorderedListItem = ChatbotAnswerParseService.parseListItem(
                line,
                ChatbotAnswerParseService.unorderedListPattern
            );
            const orderedListItem = ChatbotAnswerParseService.parseListItem(
                line,
                ChatbotAnswerParseService.orderedListPattern
            );
            const paragraph = (line || '').trim().replace(/\\./g, '.');

            if (unorderedListItem) {
                return {
                    type: 'unorderedList',
                    parts: this.parseReplacements(unorderedListItem)
                };
            }
            if (orderedListItem) {
                return {
                    type: 'orderedList',
                    parts: this.parseReplacements(orderedListItem)
                };
            }
            return {
                type: 'paragraph',
                parts: this.parseReplacements(paragraph)
            };
        });


    private static parseListItem = (text: string, pattern: RegExp): string => {
        const match = new RegExp(pattern).exec(text) || [];
        return match[1]?.trim();
    };

    private static groupElements = (elements: ChatbotElementInterface[]): ChatbotElementInterface[] =>
        elements.reduce<ChatbotElementInterface[]>((acc, current) => {
            const isListElement = current.type === 'unorderedList' || current.type === 'orderedList';
            const lastElement = acc.length > 0 ? acc[acc.length - 1] : null;
            const canMergeWithPrevious = lastElement && lastElement.type === current.type && isListElement;

            if (canMergeWithPrevious) {
                return [
                    ...acc.slice(0, -1),
                    {
                        ...lastElement,
                        parts: [...lastElement.parts, ...current.parts]
                    }
                ];
            }
            return [...acc, current];
        }, []);

    private parseReplacements = (text: string): ChatbotElementPartInterface[] => {
        const splitPattern = /(%{\w+\([^}]+\)})/g;
        const pattern = /%{(\w+)\(([^}]+)\)}/g;

        return text
            .split(splitPattern)
            .filter((part: string) => part.length)
            .map((part: string) => {
                if (!part.startsWith('%')) {
                    return {
                        type: 'text',
                        data: this.replaceTextDecorations(part)
                    };
                }
                const match = new RegExp(pattern).exec(part) || [];
                const type = match[1]?.toLowerCase();
                const id = match[2];
                
                switch (type) {
                    case 'link': {
                        const linkData = this.links.find(
                            (link: ChatbotLinkInterface) => link.id === Number(id)
                        );
                        return linkData ? { type: 'link', data: linkData } : { type: 'text', data: part };
                    }
                    
                    case 'video': {
                        const videoData = this.videos.find(
                            (video: ChatbotVideoInterface) => video.id === Number(id)
                        );
                        return videoData ? { type: 'video', data: videoData } : { type: 'text', data: part };
                    }
                    
                    case 'dialogoption':
                        return {
                            type: 'dialogoption',
                            data: {
                                text: id,
                                path: this.dialogPath,
                                icon: ''
                            }
                        };
                    
                    default:
                        return { type: 'text', data: part };
                }
            });
    };

    private replaceTextDecorations = (text: string): string =>
        text?.length
            ? ChatbotAnswerParseService.textDecorationReplacements.reduce(
                  (formattedText, { regExp, replace }) => formattedText.replace(regExp, replace),
                  text
              )
            : '';
}
